// @ts-nocheck

import React, { useEffect, useMemo, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { Podium, Ranking } from '../../assets/svg';
import { Target } from '../../client';
import usePrevious from '../../hooks/usePrevious';
import useRouter from '../../hooks/useRouter';
import { __ } from '../../i18n/l10n';
import MissionBannerContainer from '../../modules/missions/components/MissionBanner/MissionBannerContainer';
import palette from '../../styled-components/palette';
import { AutoScrollAnchor } from '../../styled-components/shared';
import { SportIds } from '../../types/sports';
import { number as NumberUtils } from '../../utils';
import { getBetRadarTournamentId, getBetRadarSeasonId } from '../../utils/betradar';
import { getCatalogRoute } from '../../utils/routes/catalog';
import RoutesRedirectionFactory from '../../utils/routes/RoutesRedirectionFactory';
import BetFiltersContainer from '../BetFilters/BetFiltersContainer';
import Breadcrumbs from '../Breadcrumbs';
import DocumentTitle from '../DocumentTitle';
import FlagIcon from '../FlagIcon';
import MatchRowList from '../MatchRowList';
import NotFoundView from '../NotFoundView';
import PromoDisplayer from '../PromoDisplayer';
import SportIcon from '../SportIcon';
import WinaLoader from '../WinaLoader';

const NoOutlineSection = styled.section`
  & .ReactVirtualized__Grid.ReactVirtualized__List {
    outline: none !important;
  }
`;

const DocumentContent = styled.div``;

const StyledFlagIcon = styled(FlagIcon)`
  transform: scale(1.05);
`;

const StyledMissionBannerContainer = styled(MissionBannerContainer)`
  ${Target.__MOBILE__
    ? css`
        padding: 0.2em 0.6em;
        margin-top: 0.4em;
      `
    : css`
        padding: 0.2em 0;
        margin-top: 0.6em;
      `}
`;

const StyledBetFiltersContainer = styled(BetFiltersContainer)`
  margin-top: 0.6em;
`;

const WidgetCommandKeys = {
  LIVE_TABLE: 'season.liveTable',
  CUP_ROSTER: 'season.cupRoster',
};

const IconButtonWrapper = styled.div`
  height: 2.8em;
  width: 2.8em;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 1.5em;
  background-color: ${({ $isOpen }) => ($isOpen ? palette.bluegrey800 : palette.bluegrey900)};
  cursor: pointer;
`;

const RankingButton = styled(Ranking)`
  display: flex;
`;

const PodiumButton = styled(Podium)`
  display: flex;
`;

export const CATALOG_ROUTE_ANCHOR_ID = 'catalog-route-anchor';

const StyledPromoDisplayer = styled(PromoDisplayer)`
  padding: 0 0.7em;

  &&&& {
    color: white;
    a {
      width: 100%;
      padding: 0;
      display: block;
    }
    img {
      max-width: 100%;
      border-radius: 1em;
      display: block;
      padding: 0;
    }
  }
`;

const RoutesCatalog = ({
  sport,
  category,
  tournament,
  view: { isNull, isEmpty, list } = { isNull: false, isEmpty: true, list: [] },
  anchorMatchId,
  askRoute,
  betFilterId,
  isLoading = false,
  missionId,
}) => {
  const {
    history,
    params: { sportId, categoryId, tournamentId },
  } = useRouter();

  const route = useMemo(
    () => getCatalogRoute({ sportId, categoryId, tournamentId }),
    [categoryId, sportId, tournamentId],
  );

  const lastRouteRef = useRef(null);
  const previousSportId = usePrevious(sportId);
  const sportIdChanged = useMemo(
    () => previousSportId !== undefined && sportId !== previousSportId,
    [previousSportId, sportId],
  );

  useEffect(() => {
    const betFilterRouteSuffix = betFilterId ? `-${betFilterId}` : '';
    let currentRouteRef = `${route}${betFilterRouteSuffix}`;
    if (sportIdChanged) {
      currentRouteRef = route;
      askRoute({ route });
      lastRouteRef.current = route;
    } else if (lastRouteRef.current === null || lastRouteRef.current !== currentRouteRef) {
      askRoute({ route, betFilterId });
      lastRouteRef.current = currentRouteRef;
    }
  }, [askRoute, betFilterId, sportIdChanged, route]);

  useEffect(() => {
    if (!isLoading && !isNull && !isEmpty && list.length) {
      const filteredList = list.filter(({ type }) => type !== 'separator');
      if (filteredList.length) {
        const match = filteredList[0];
        const { matchId, isOutright } = match;
        // NOTE: redirection vers match route si il n'y qu'un match
        // outright dans la liste
        // Et que ce n'est pas le sport des cotes boostés
        if (
          isOutright === true &&
          filteredList.length === 1 &&
          parseInt(sportId) !== SportIds.SPECIAL_ODDS &&
          !betFilterId
        ) {
          history.replace(RoutesRedirectionFactory.getMatchRouteConfig(matchId));
        }
      }
    }
  }, [isEmpty, isLoading, isNull, list, history, sportId, betFilterId]);

  const documentTitle = useMemo(() => {
    let docTitle = __('!desc:DocumentTitle');
    if (isLoading === false && sport) {
      const { sportName } = sport;
      if (!isNull && !!sportName) {
        docTitle = `${sportName} - `;
        if (category) {
          const { categoryName } = category;
          docTitle += categoryName ? `${categoryName} - ` : '';
        }
        if (tournament) {
          const { tournamentName } = tournament;
          docTitle += tournamentName ? `${tournamentName} - ` : '';
        }
        docTitle += __('!desc:DocumentTitle');
      }
    }
    return docTitle;
  }, [category, isLoading, isNull, sport, tournament]);

  const breadcrumbsLabel = useMemo(() => {
    let result = '';
    let hasPrevious = false;
    if (category?.categoryName) {
      hasPrevious = true;
      result = result.concat(`${category.categoryName}`);
    }

    if (tournament?.tournamentName) {
      if (tournament?.topName) {
        result = tournament?.topName;
      } else {
        const separator = hasPrevious ? ' - ' : '';
        result = result.concat(`${separator}${tournament?.tournamentName}`);
      }
      hasPrevious = true;
    }

    if (!hasPrevious && sport?.sportName) {
      return sport.sportName;
    }

    return result;
  }, [sport, category, tournament]);

  const breadcrumbsFlagId = useMemo(() => tournament?.flag || category?.flag || undefined, [tournament, category]);
  const breadcrumbsIcons = useMemo(() => {
    const res = [];

    if (sportId) {
      res.push(<SportIcon sportId={NumberUtils.castNumber(sportId)} />);
    }

    if (breadcrumbsFlagId) {
      res.push(<StyledFlagIcon flagId={breadcrumbsFlagId.toLowerCase()} />);
    }

    return res.length ? res : undefined;
  }, [breadcrumbsFlagId, sportId]);

  const firstMatchIdInList = useMemo(() => list[0]?.matchId, [list]);

  const breadcrumbsOptions = useMemo(() => {
    const res = [];
    const { srSeasonId, srTournamentId, displayCupRoster, displayLiveTable } = tournament ?? {};
    const betRadarTournamentId = getBetRadarTournamentId(srTournamentId);
    const betRadarSeasonId = getBetRadarSeasonId(srSeasonId);

    if (displayLiveTable && betRadarTournamentId) {
      res.push(
        <NavLink
          to={{
            hash: '#liveTable',
            state: {
              brWidgetProps: {
                commandKey: WidgetCommandKeys.LIVE_TABLE,
                widgetOptions: {
                  uniqueTournamentId: betRadarTournamentId,
                  disableWidgetHeader: true,
                },
              },
            },
          }}
        >
          <IconButtonWrapper>
            <PodiumButton />
          </IconButtonWrapper>
        </NavLink>,
      );
    }

    if (displayCupRoster && betRadarSeasonId && firstMatchIdInList) {
      res.push(
        <NavLink
          to={{
            hash: '#cupRoster',
            state: {
              brWidgetProps: {
                commandKey: WidgetCommandKeys.CUP_ROSTER,
                widgetOptions: {
                  matchId: firstMatchIdInList,
                  seasonId: betRadarSeasonId,
                  disableWidgetHeader: true,
                },
              },
            },
          }}
        >
          <IconButtonWrapper>
            <RankingButton />
          </IconButtonWrapper>
        </NavLink>,
      );
    }

    return res.length ? res : undefined;
  }, [tournament, firstMatchIdInList]);

  const Promotion = useMemo(() => {
    if (list.length) {
      const promotion =
        tournament?.livePromo ||
        tournament?.prematchPromo ||
        category?.livePromo ||
        category?.prematchPromo ||
        sport?.livePromo ||
        sport?.prematchPromo;

      const warnings = ((): Array<string> => {
        const arr = [];
        if (sport?.warning) {
          arr.push(sport.warning);
        }
        if (category?.warning) {
          arr.push(category.warning);
        }
        if (tournament?.warning) {
          arr.push(tournament.warning);
        }
        return arr;
      })();

      return <StyledPromoDisplayer texts={promotion} warnings={warnings} />;
    }
    return null;
  }, [list, sport, category, tournament]);

  return (
    <DocumentTitle title={documentTitle}>
      <DocumentContent>
        <AutoScrollAnchor data-anchorid={CATALOG_ROUTE_ANCHOR_ID} />
        {breadcrumbsLabel ? (
          <Breadcrumbs icons={breadcrumbsIcons} label={breadcrumbsLabel} options={breadcrumbsOptions} />
        ) : null}
        <NoOutlineSection>
          {!isLoading && !isNull && !isEmpty ? Promotion : null}
          {!isLoading && !isNull && !isEmpty && missionId ? (
            <StyledMissionBannerContainer missionId={missionId} />
          ) : null}
          <StyledBetFiltersContainer />
          {isLoading ? <WinaLoader delayed /> : null}
          {!isLoading && !isNull && !isEmpty ? (
            <MatchRowList items={list} anchorMatchId={anchorMatchId} largeMissionCard />
          ) : null}
          {!isLoading && (isNull || isEmpty) ? <NotFoundView label={__('no events')} /> : null}
        </NoOutlineSection>
      </DocumentContent>
    </DocumentTitle>
  );
};

export default RoutesCatalog;
